// PURPOSE: Right panel container with tab navigation
// TABS: Chart, Notes, Images/Attachments

import React, { useMemo, useState } from 'react';
import { Button, Tabs } from 'antd';
import PropTypes from 'prop-types';

import {
    FileTextOutlined,
    LineChartOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    PictureOutlined
} from '@ant-design/icons';

import TVChartContainer from '../../../../components/tradingview/full-chart';
import TradeNotes from '../../trade-notes';

import executionUnixSeconds from './chart-time';
import ImagesTab from './ImagesTab';

import './styles.scss';

export default function RightPanel({
    trade,
    tradeId,
    tradeNotes,
    setTradeNotes,
    isViewAsActive,
    timezone,
    isLeftPanelCollapsed,
    onToggleLeftPanel
}) {
    const [activeTab, setActiveTab] = useState('chart');

    // Format symbol for chart
    const formattedSymbol =
        trade.assetType === 'Option' ? trade.optionContract?.underlying : trade.symbol;

    // Extract all images from notes for the Images tab
    const allImages = useMemo(() => {
        if (!tradeNotes || !Array.isArray(tradeNotes)) return [];
        return tradeNotes
            .filter((note) => note.images && note.images.length > 0)
            .flatMap((note) =>
                note.images.map((img) => ({
                    ...img,
                    noteId: note._id,
                    noteDate: note.createDate
                }))
            );
    }, [tradeNotes]);

    const tabItems = [
        {
            key: 'chart',
            label: (
                <span>
                    <LineChartOutlined />
                    Chart
                </span>
            ),
            children: trade.executions ? (
                <div className="chart-container">
                    <TVChartContainer
                        symbol={formattedSymbol}
                        startDate={executionUnixSeconds(trade.executions[0].date, timezone)}
                        endDate={executionUnixSeconds(
                            trade.executions[trade.executions.length - 1].date,
                            timezone
                        )}
                        timezone={timezone}
                    />
                </div>
            ) : (
                <div className="no-chart-data">No execution data available for chart</div>
            )
        },
        {
            key: 'notes',
            label: (
                <span>
                    <FileTextOutlined />
                    Notes
                </span>
            ),
            children:
                tradeNotes !== null ? (
                    <TradeNotes
                        tradeId={tradeId}
                        setTradeNotes={setTradeNotes}
                        isViewAsActive={isViewAsActive}
                        tradeNotes={tradeNotes}
                    />
                ) : null
        },
        {
            key: 'images',
            label: (
                <span>
                    <PictureOutlined />
                    Images ({allImages.length})
                </span>
            ),
            children: <ImagesTab images={allImages} />
        }
    ];

    const collapseButton = (
        <Button
            className="panel-collapse-toggle"
            type="text"
            icon={isLeftPanelCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={onToggleLeftPanel}
            title={isLeftPanelCollapsed ? 'Expand left panel' : 'Collapse left panel'}
        />
    );

    return (
        <div className="right-panel-container">
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                className="right-panel-tabs"
                tabPosition="top"
                tabBarExtraContent={{ left: collapseButton }}
            />
        </div>
    );
}

RightPanel.propTypes = {
    trade: PropTypes.object.isRequired,
    tradeId: PropTypes.string.isRequired,
    tradeNotes: PropTypes.array,
    setTradeNotes: PropTypes.func.isRequired,
    isViewAsActive: PropTypes.bool,
    timezone: PropTypes.string,
    isLeftPanelCollapsed: PropTypes.bool,
    onToggleLeftPanel: PropTypes.func
};
